import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';

export function Company() {
  const { t } = useLanguage();
  const stats = t('company.stats');

  return (
    <section id="about" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <picture>
          <source srcSet="/images/company-bg.webp" type="image/webp" />
          <img 
            src="/images/company-bg.jpg"
            alt="Team collaboration"
            className="w-full h-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/95 to-slate-950/80" />
      </div>

      {/* Accent lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-primary font-mono text-sm tracking-wider uppercase mb-4 block">
              About us
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
              {t('company.title')}
            </h2>
            <p className="text-xl text-muted-foreground mb-10 leading-relaxed">
              {t('company.description')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8">
              <motion.div 
                data-testid="stat-years"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-center md:text-left"
              >
                <p className="font-heading text-4xl md:text-5xl font-black bg-gradient-to-br from-primary to-purple-500 bg-clip-text text-transparent">
                  {stats.years}
                </p>
                <p className="text-muted-foreground mt-1">{stats.yearsLabel}</p>
              </motion.div>
              <motion.div 
                data-testid="stat-projects"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-center md:text-left"
              >
                <p className="font-heading text-4xl md:text-5xl font-black bg-gradient-to-br from-primary to-purple-500 bg-clip-text text-transparent">
                  {stats.projects}
                </p>
                <p className="text-muted-foreground mt-1">{stats.projectsLabel}</p>
              </motion.div>
              <motion.div 
                data-testid="stat-clients"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-center md:text-left"
              >
                <p className="font-heading text-4xl md:text-5xl font-black bg-gradient-to-br from-primary to-purple-500 bg-clip-text text-transparent">
                  {stats.clients}
                </p>
                <p className="text-muted-foreground mt-1">{stats.clientsLabel}</p>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            {/* Image grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-square rounded-2xl overflow-hidden border border-slate-800 shadow-2xl">
                  <picture>
                    <source srcSet="/images/team-1.webp" type="image/webp" />
                    <img 
                      src="/images/team-1.jpg"
                      alt="Team working"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </picture>
                </div>
                <div className="aspect-video rounded-2xl overflow-hidden border border-slate-800 shadow-2xl">
                  <picture>
                    <source srcSet="/images/team-2.webp" type="image/webp" />
                    <img 
                      src="/images/team-2.jpg"
                      alt="Development process"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </picture>
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-video rounded-2xl overflow-hidden border border-slate-800 shadow-2xl">
                  <picture>
                    <source srcSet="/images/team-3.webp" type="image/webp" />
                    <img 
                      src="/images/team-3.jpg"
                      alt="Coding"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </picture>
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden border border-slate-800 shadow-2xl">
                  <picture>
                    <source srcSet="/images/team-4.webp" type="image/webp" />
                    <img 
                      src="/images/team-4.jpg"
                      alt="Team collaboration"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </picture>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm text-slate-300">{t('company.acceptingProjects')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
